import {
  ComplianceControlChecks,
  ComplianceExceptionList,
  ComplianceRefreshResult,
  CreateComplianceExceptionResult,
  type ComplianceExceptionQuery,
  type ComplianceView,
  type CreateComplianceException,
  ComplianceStandardView,
  ComplianceSummary,
  type ComplianceScope,
  type ComplianceStandard,
} from '@bartizo/shared';
import { z } from 'zod';
import { apiRequest } from './client.js';

const scoped = (scope: ComplianceView, extra: Record<string, string> = {}): string => {
  const params = new URLSearchParams(extra);
  if (scope.server !== undefined) params.set('server', scope.server);
  if (scope.group !== undefined) params.set('group', scope.group);
  // Unscoped is `all`: the address stays as it was.
  if (scope.level !== undefined && scope.level !== 'all') params.set('level', scope.level);
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
};

/** P11.8.9: each standard's fleet score, of all servers or of one server or group. */
export const getComplianceSummary = (scope: ComplianceView = {}): Promise<ComplianceSummary> =>
  apiRequest(`/compliance${scoped(scope)}`, { schema: ComplianceSummary });

/** One standard: its controls, what to fix first, and each server's score. */
export const getComplianceStandard = (
  standard: ComplianceStandard,
  scope: ComplianceView = {},
): Promise<ComplianceStandardView> =>
  apiRequest(`/compliance/${encodeURIComponent(standard)}${scoped(scope)}`, {
    schema: ComplianceStandardView,
  });

/** The checks behind one control: what each asks, why, how to fix it, where it fails. */
export const getComplianceControlChecks = (
  standard: ComplianceStandard,
  control: { control: string; policyId: string | null },
  scope: ComplianceView = {},
): Promise<ComplianceControlChecks> =>
  apiRequest(
    `/compliance/${encodeURIComponent(standard)}/checks${scoped(scope, {
      control: control.control,
      ...(control.policyId === null ? {} : { policy: control.policyId }),
    })}`,
    { schema: ComplianceControlChecks },
  );

/** P11.19: read the shown servers' findings from the manager now, not at the next ten-minute read. */
export const refreshCompliance = (scope: ComplianceScope = {}): Promise<ComplianceRefreshResult> =>
  apiRequest('/compliance/refresh', {
    method: 'POST',
    body: scope,
    schema: ComplianceRefreshResult,
  });

/** P11.20: what people decided about failing checks. */
export const listComplianceExceptions = (
  query: ComplianceExceptionQuery = {},
): Promise<ComplianceExceptionList> =>
  apiRequest(
    `/compliance/exceptions${scoped(query, query.status === undefined ? {} : { status: query.status })}`,
    { schema: ComplianceExceptionList },
  );

/** Admin: record a decision for the servers in scope where the check fails. */
export const recordComplianceException = (
  body: CreateComplianceException,
): Promise<CreateComplianceExceptionResult> =>
  apiRequest('/compliance/exceptions', {
    method: 'POST',
    body,
    schema: CreateComplianceExceptionResult,
  });

/** Admin: take a decision back; the check counts again. */
export const removeComplianceException = (id: string): Promise<{ removed: true }> =>
  apiRequest(`/compliance/exceptions/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    schema: z.object({ removed: z.literal(true) }),
  });
